import React, { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { AlgorithmAnalytics } from '../components/analytics/AlgorithmAnalytics';
import { ProfitOverTimeChart } from '../components/analytics/ProfitOverTimeChart';
import { EnhancedTradeHistory } from '../components/trading/EnhancedTradeHistory';
import { tradingService } from '../services/trading';
import { accountsService } from '../services/accounts';
import { getStartOfDayPacific, getStartOfWeekPacific, getStartOfMonthPacific, getStartOfYearPacific } from '@trading-app/shared';

/**
 * Algorithm Trading Analytics Page
 *
 * This page displays:
 * 1. Algorithm performance analytics dashboard (top)
 * 2. Profit over time chart with shared filters
 * 3. Enhanced trade history with pagination and filters (bottom)
 */
export function AlgorithmTradingPage() {
  const { user, logout } = useAuth();
  const location = useLocation();

  // Shared filter state
  const [accountFilter, setAccountFilter] = useState('');
  const [tickerFilter, setTickerFilter] = useState('');
  const [algorithmFilter, setAlgorithmFilter] = useState('');
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month' | 'year' | 'all'>('all');

  // Fetch accounts for account filter
  const { data: accounts = [], isLoading: isLoadingAccounts, error: accountsError } = useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      try {
        return await accountsService.getAccounts();
      } catch (err) {
        console.error('Accounts fetch error:', err);
        return [];
      }
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch trade history for filters
  const { data: trades = [], isLoading: isLoadingTrades, error: tradesError } = useQuery({
    queryKey: ['tradeHistory'],
    queryFn: async () => {
      try {
        return await tradingService.getTradeHistory();
      } catch (err) {
        console.error('Trade history fetch error:', err);
        return [];
      }
    },
    retry: 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Get unique tickers and algorithms for filter dropdowns
  const uniqueTickers = useMemo(() => {
    const tickers = new Set(trades.map((trade: any) => trade.ticker));
    return Array.from(tickers).sort();
  }, [trades]);

  const uniqueAlgorithms = useMemo(() => {
    const algorithms = new Set(trades.map((trade: any) => trade.algorithm || 'Manual'));
    return Array.from(algorithms).sort();
  }, [trades]);

  const timeFilters = [
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'week' },
    { label: 'This Month', value: 'month' },
    { label: 'This Year', value: 'year' },
    { label: 'All Time', value: 'all' },
  ];

  return (
    <div>

        {/* Loading State */}
        {(isLoadingAccounts || isLoadingTrades) && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <div className="text-gray-500 dark:text-gray-400">Loading performance data...</div>
            </div>
          </div>
        )}

        {/* Error State */}
        {(accountsError || tradesError) && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-8">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Error loading data
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {accountsError && <p>Failed to load accounts: {accountsError.message}</p>}
                  {tradesError && <p>Failed to load trade history: {tradesError.message}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {!isLoadingAccounts && !isLoadingTrades && !accountsError && !tradesError && (
        <div className="space-y-8">
          {/* Shared Filters */}
          <div className="rh-card p-4 sm:p-6 rh-hover-shadow rh-animate-fade-in">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h2>

              {/* Filter Dropdowns - Stack on mobile, row on larger screens */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                {/* Account Filter (Global) */}
                <select
                  value={accountFilter}
                  onChange={(e) => setAccountFilter(e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm font-medium"
                >
                  <option value="">All Accounts</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.accountName} ({account.brokerageName})
                    </option>
                  ))}
                </select>

                {/* Ticker Filter */}
                <select
                  value={tickerFilter}
                  onChange={(e) => setTickerFilter(e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  <option value="">All Stocks</option>
                  {uniqueTickers.map((ticker) => (
                    <option key={ticker} value={ticker}>
                      {ticker}
                    </option>
                  ))}
                </select>

                {/* Algorithm Filter */}
                <select
                  value={algorithmFilter}
                  onChange={(e) => setAlgorithmFilter(e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  <option value="">All Algorithms</option>
                  {uniqueAlgorithms.map((algo) => (
                    <option key={algo} value={algo}>
                      {algo}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time Range Filter - Wrap on mobile */}
              <div className="flex flex-wrap gap-2">
                {timeFilters.map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setTimeRange(filter.value as any)}
                    className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      timeRange === filter.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Profit Over Time Chart */}
          <section>
            <ProfitOverTimeChart
              accountFilter={accountFilter}
              tickerFilter={tickerFilter}
              algorithmFilter={algorithmFilter}
              timeRange={timeRange}
            />
          </section>

          {/* Algorithm Analytics Dashboard */}
          <section>
            <div className="rh-card p-6 rh-hover-shadow rh-animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Algorithm Performance</h2>
              <AlgorithmAnalyticsFiltered
                accountFilter={accountFilter}
                tickerFilter={tickerFilter}
                algorithmFilter={algorithmFilter}
                timeRange={timeRange}
              />
            </div>
          </section>

          {/* Enhanced Trade History with Pagination and Filters */}
          <section>
            <EnhancedTradeHistoryFiltered
              accountFilter={accountFilter}
              tickerFilter={tickerFilter}
              algorithmFilter={algorithmFilter}
              timeRange={timeRange}
            />
          </section>
        </div>
        )}
    </div>
  );
}

// Wrapper component for AlgorithmAnalytics with shared filters
function AlgorithmAnalyticsFiltered({ accountFilter, tickerFilter, algorithmFilter, timeRange }: any) {
  const { data: trades = [], isLoading } = useQuery({
    queryKey: ['tradeHistory'],
    queryFn: async () => {
      try {
        return await tradingService.getTradeHistory();
      } catch (err) {
        console.error('Trade history fetch error:', err);
        return [];
      }
    },
  });

  // Apply filters
  const filteredTrades = useMemo(() => {
    let filtered = [...trades];

    // Apply account filter (global filter - affects all other filters)
    if (accountFilter) {
      filtered = filtered.filter((trade: any) => trade.accountId === accountFilter);
    }

    if (tickerFilter) {
      filtered = filtered.filter((trade: any) => trade.ticker === tickerFilter);
    }

    if (algorithmFilter) {
      filtered = filtered.filter((trade: any) => (trade.algorithm || 'Manual') === algorithmFilter);
    }

    if (timeRange !== 'all') {
      let cutoffDate: Date;

      switch (timeRange) {
        case 'today':
          cutoffDate = getStartOfDayPacific();
          break;
        case 'week':
          cutoffDate = getStartOfWeekPacific();
          break;
        case 'month':
          cutoffDate = getStartOfMonthPacific();
          break;
        case 'year':
          cutoffDate = getStartOfYearPacific();
          break;
        default:
          cutoffDate = getStartOfDayPacific();
      }

      filtered = filtered.filter((trade: any) => {
        const tradeDate = new Date(trade.executedAt || trade.createdAt);
        return tradeDate >= cutoffDate;
      });
    }

    return filtered;
  }, [trades, accountFilter, tickerFilter, algorithmFilter, timeRange]);

  // Pass filters to AlgorithmAnalytics (it queries trade_aggregates directly)
  return <AlgorithmAnalyticsContent accountFilter={accountFilter} tickerFilter={tickerFilter} algorithmFilter={algorithmFilter} timeRange={timeRange} />;
}

// Wrapper component for EnhancedTradeHistory with shared filters
function EnhancedTradeHistoryFiltered({ accountFilter, tickerFilter, algorithmFilter, timeRange }: any) {
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const { data: trades = [], isLoading, error } = useQuery({
    queryKey: ['tradeHistory'],
    queryFn: async () => {
      try {
        return await tradingService.getTradeHistory();
      } catch (err) {
        console.error('Trade history fetch error:', err);
        return [];
      }
    },
  });

  // Apply filters
  const filteredTrades = useMemo(() => {
    let filtered = [...trades];

    // Apply account filter (global filter - affects all other filters)
    if (accountFilter) {
      filtered = filtered.filter((trade: any) => trade.accountId === accountFilter);
    }

    if (tickerFilter) {
      filtered = filtered.filter((trade: any) => trade.ticker === tickerFilter);
    }

    if (algorithmFilter) {
      filtered = filtered.filter((trade: any) => (trade.algorithm || 'Manual') === algorithmFilter);
    }

    if (timeRange !== 'all') {
      let cutoffDate: Date;

      switch (timeRange) {
        case 'today':
          cutoffDate = getStartOfDayPacific();
          break;
        case 'week':
          cutoffDate = getStartOfWeekPacific();
          break;
        case 'month':
          cutoffDate = getStartOfMonthPacific();
          break;
        case 'year':
          cutoffDate = getStartOfYearPacific();
          break;
        default:
          cutoffDate = getStartOfDayPacific();
      }

      filtered = filtered.filter((trade: any) => {
        const tradeDate = new Date(trade.executedAt || trade.createdAt);
        return tradeDate >= cutoffDate;
      });
    }

    return filtered;
  }, [trades, accountFilter, tickerFilter, algorithmFilter, timeRange]);

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [accountFilter, tickerFilter, algorithmFilter, timeRange]);

  // Use existing EnhancedTradeHistory logic with filtered data
  return <EnhancedTradeHistoryContent
    trades={filteredTrades}
    isLoading={isLoading}
    error={error}
    currentPage={currentPage}
    setCurrentPage={setCurrentPage}
    itemsPerPage={ITEMS_PER_PAGE}
  />;
}

// Content components that receive filtered data
function AlgorithmAnalyticsContent({ accountFilter, tickerFilter, algorithmFilter, timeRange }: any) {
  // Pass filters to AlgorithmAnalytics (it will fetch from trade_aggregates)
  // showSummaryCards=false because the summary cards are now on the Dashboard
  return <AlgorithmAnalytics accountFilter={accountFilter} tickerFilter={tickerFilter} algorithmFilter={algorithmFilter} timeRange={timeRange} showSummaryCards={false} />;
}

function EnhancedTradeHistoryContent({ trades, isLoading, error, currentPage, setCurrentPage, itemsPerPage }: any) {
  // Pass filtered trades to EnhancedTradeHistory via props
  return <EnhancedTradeHistory filteredTrades={trades} isLoading={isLoading} currentPage={currentPage} setCurrentPage={setCurrentPage} itemsPerPage={itemsPerPage} />;
}